import { useMemo, useState, useCallback } from "react"
import { toast } from "react-toastify"
import { Badge, Col, Row } from "reactstrap"

// Custom Components
import { CanView } from "components/Common/CanView"
import ButtonTooltip from "components/Common/ButtonTooltip"
import TableContainer from "components/Common/TableContainer"
import DeleteModal from "components/Common/DeleteModal"
import GlobalInputSearch from "components/Common/GlobalInputSearch"

// Hooks
import { useListParams } from "hooks/useListParams"

// Utils
import { formatReadableDate } from "utils/formatDate"
import CustomerAddEdit from "./CustomerAddEdit"
import { CustomerHeader } from "./CustomerHeader"
import PageWrapper from "components/Common/PageWrapper"
import {
  formatCustomerTypeLabel,
  getCustomerDepartmentText,
  getCustomerOrganizationText,
  resolveCustomerTypeKey,
} from "helpers/customerOnboarding"
import { useCustomers, useDeleteCustomer } from "hooks/useCustomer"
import { useNavigate } from "react-router-dom"
import { ACCESS } from "constants/accessPermissions"

const CustomerList = () => {
  document.title = "Customer List"

  // State
  const [modal, setModal] = useState(false)
  const [isEdit, setIsEdit] = useState(false)
  const [fetchedData, setFetchedData] = useState(null)
  const [deleteModal, setDeleteModal] = useState(false)

  // URL search params hook
  const { params, setPage, setSearch, resetParams, hasActiveFilters } =
    useListParams({
      page: 1,
      search: "",
    })

  // Hooks
  const navigate = useNavigate()

  // Query params
  const queryParams = useMemo(() => {
    const { page = 1, per_page = 10, search } = params
    return { page, per_page, search }
  }, [params])

  // React Query hooks
  const {
    data: fetchedResponseData,
    isLoading,
    refetch: refetchResponseData,
    isRefetching,
  } = useCustomers(queryParams)

  const { mutateAsync: deleteFetchedData, isPending: deleteLoading } =
    useDeleteCustomer()

  // Toggle modal
  const toggleModal = useCallback(() => {
    if (modal) {
      setModal(false)
      setFetchedData(null)
      setIsEdit(false)
    } else setModal(true)
  }, [modal])

  const handleAddNewClick = useCallback(() => {
    setFetchedData(null)
    setIsEdit(false)
    toggleModal()
  }, [toggleModal])

  const handleEditClick = useCallback(
    item => {
      setFetchedData(item)
      setIsEdit(true)
      toggleModal()
    },
    [toggleModal]
  )

  const handleViewClick = useCallback(
    item => {
      navigate(`/customer-list/${item?.cuid}/view`)
    },
    [navigate]
  )

  const handleDeleteClick = useCallback(item => {
    setFetchedData(item)
    setDeleteModal(true)
  }, [])

  const handleDelete = useCallback(async () => {
    if (!fetchedData?.id) return
    try {
      const res = await deleteFetchedData(fetchedData.id)
      toast.success(res?.message ?? "Customer deleted successfully.")
      setDeleteModal(false)
      refetchResponseData()
    } catch (error) {
      console.error(error)
      toast.error("Failed to delete customer.")
    }
  }, [fetchedData, deleteFetchedData, refetchResponseData])

  const handleRefresh = () => refetchResponseData()

  // Table columns
  const columns = useMemo(
    () => [
      {
        header: "Customer",
        cell: ({ row }) => {
          const data = row.original
          const typeKey = resolveCustomerTypeKey(data.customer_type)
          const orgText = getCustomerOrganizationText(data)
          const deptText = getCustomerDepartmentText(data)
          const quotationCount = data.quotations?.length ?? 0
          const receiptCount = data.money_receipts?.length ?? 0
          return (
            <div style={{ wordBreak: "break-word", overflowWrap: "break-word" }}>
              <div className="fw-semibold">{data.name || "-"}</div>
              <small className="text-muted">{data.cuid || "No customer ID"}</small>
              <div className="mt-2 d-flex flex-wrap gap-2">
                {data.customer_type ? (
                  <Badge color="light" className="text-primary border">
                    {formatCustomerTypeLabel(data.customer_type)}
                  </Badge>
                ) : null}
                {data.designation ? (
                  <Badge color="light" className="text-muted border">
                    {data.designation}
                  </Badge>
                ) : null}
              </div>
              {typeKey !== "general" && orgText ? (
                <small className="text-muted d-block mt-1">
                  <i className="bx bx-buildings me-1" />
                  {orgText}
                  {deptText ? <span className="ms-1">&middot; {deptText}</span> : null}
                </small>
              ) : null}
              <div className="d-sm-none mt-1">
                <small className="text-muted d-block">
                  <i className="bx bx-phone me-1" />{data.mobile || "-"}
                </small>
                <small className="text-muted d-block">
                  <i className="bx bx-envelope me-1" />{data.email || "-"}
                </small>
              </div>
              {data.full_address ? (
                <small className="text-muted d-block mt-1 d-lg-none">
                  <i className="bx bx-map-pin me-1" />
                  {data.full_address}
                </small>
              ) : null}
              <small className="text-muted d-block mt-1 d-lg-none">
                <i className="bx bx-file me-1" />{quotationCount} quotations
                <span className="ms-2">
                  <i className="bx bx-receipt me-1" />{receiptCount} receipts
                </span>
              </small>
            </div>
          )
        },
      },
      {
        header: "Contact",
        meta: { className: "d-none d-sm-table-cell" },
        cell: ({ row }) => {
          const data = row.original
          return (
            <div style={{ wordBreak: "break-word", overflowWrap: "break-word" }}>
              <div>
                <i className="bx bx-phone" /> {data.mobile || "-"}
              </div>
              <div>
                <i className="bx bx-envelope" /> {data.email || "-"}
              </div>
            </div>
          )
        },
      },
      {
        header: "Address",
        meta: { style: { width: "30%" }, className: "d-none d-lg-table-cell" },
        cell: ({ row }) => {
          const data = row.original

          return (
            <div style={{ wordBreak: "break-word", overflowWrap: "break-word" }}>
              {data.full_address || "-"}
            </div>
          )
        },
      },
      {
        header: "Activity",
        meta: { className: "d-none d-lg-table-cell" },
        cell: ({ row }) => {
          const data = row.original
          return (
            <div style={{ wordBreak: "break-word", overflowWrap: "break-word" }}>
              <div>
                <i className="bx bx-file" /> {data.quotations?.length ?? 0} quotations
              </div>
              <div>
                <i className="bx bx-receipt" /> {data.money_receipts?.length ?? 0} receipts
              </div>
              <small className="text-muted">
                {data.created_at ? formatReadableDate(data.created_at) : "-"}
              </small>
            </div>
          )
        },
      },
      {
        header: "Action",
        meta: { className: "table-col-action" },
        cell: ({ row }) => {
          const data = row.original
          return (
            <ul className="list-unstyled d-flex flex-wrap gap-1 mb-0">
              <CanView access={[ACCESS.CUSTOMER.VIEW_LIST]}>
                <li>
                  <ButtonTooltip
                    type="view"
                    btnId={`customer-view-${data.cuid}`}
                    onClick={() => handleViewClick(data)}
                  />
                </li>
              </CanView>
              <CanView access={[ACCESS.CUSTOMER.EDIT]}>
                <li>
                  <ButtonTooltip
                    type="edit"
                    btnId={`customer-edit-${data.id}`}
                    onClick={() => handleEditClick(data)}
                  />
                </li>
              </CanView>
              <CanView access={[ACCESS.CUSTOMER.DELETE]}>
                <li>
                  <ButtonTooltip
                    type="delete"
                    btnId={`customer-delete-${data.id}`}
                    onClick={() => handleDeleteClick(data)}
                  />
                </li>
              </CanView>
            </ul>
          )
        },
      },
    ],
    [handleDeleteClick, handleEditClick, handleViewClick]
  )

  const data = fetchedResponseData?.data?.data || []
  const normalizedData = useMemo(
    () =>
      data.map(item => ({
        ...item,
        quotations: item?.quotations || [],
        money_receipts: item?.money_receipts || [],
      })),
    [data]
  )
  const paginationInfo = fetchedResponseData?.data

  return (
    <PageWrapper title="Customer List" breadcrumbItem="Customers">
      <CustomerHeader
        handleAddNewClick={handleAddNewClick}
        handleRefresh={handleRefresh}
        resetParams={resetParams}
        isRefetching={isRefetching}
        hasActiveFilters={hasActiveFilters}
        total={paginationInfo?.total ?? 0}
      />

      <DeleteModal
        show={deleteModal}
        onDeleteClick={handleDelete}
        loading={deleteLoading}
        onCloseClick={() => setDeleteModal(false)}
      />

      <Row className="my-3">
        <Col xs={12} sm={6} md={4}>
          <GlobalInputSearch value={params.search} onChange={setSearch} />
        </Col>
      </Row>

      <TableContainer
        columns={columns}
        data={normalizedData}
        isPagination={true}
        paginationInfo={paginationInfo}
        onPageChange={setPage}
        loading={isLoading || isRefetching}
        tableClass="align-middle dt-responsive w-100 table-check dataTable no-footer dtr-inline mt-4 border-top"
      />

      <CustomerAddEdit
        modal={modal}
        isEdit={isEdit}
        editedData={fetchedData}
        toggleModal={toggleModal}
      />
    </PageWrapper>
  )
}

export default CustomerList
